import Link from 'next/link';
import React from 'react';
import config from '../config';

const COLLECTIONS_LINKS = [
  { title: 'Marketplace', href: '/marketplace' },
  { title: 'First Litter', href: '/first-litter' },
  { title: 'Second Litter', href: '/second-litter' },
  { title: 'Third Litter', href: '/third-litter' },
  { title: 'Fourth Litter', href: '/fourth-litter' },
  { title: 'Shitty Cities', href: '/cities' },
  { title: 'Shitty City Blocks', href: '/blocks' },
];

const UTILITIES_LINKS = [
  { title: 'City Builder', href: '/city-builder' },
  { title: 'Trait Swapper', href: '/trait-swapper' },
  { title: 'Trait Shuffle', href: '/trait-shuffle' },
  { title: 'Banner Builder', href: '/banner-builder' },
  { title: 'Meme Generator', href: '/meme-generator' },
  { title: 'Shitty Jobs', href: '/jobs' },
  { title: 'Vending Machine', href: '/vending-machine' },
];

const COMMUNITY_LINKS = [
  { title: 'SK Club', href: '/sk-club' },
  { title: 'Discord', href: config.discord, external: true },
  { title: '𝕏 (Twitter)', href: config.twitter, external: true },
  { title: 'YouTube', href: 'https://www.youtube.com/@Shitty_Kitties', external: true },
  { title: 'Instagram', href: 'https://www.instagram.com/shittykitties_', external: true },
  { title: 'TikTok', href: 'https://www.tiktok.com/@shitty.kitties', external: true },
];

const RESOURCES_LINKS = [
  { title: 'Shitty Docs', href: config.docs, external: true },
  { title: 'Support', href: '/support' },
  { title: 'Privacy Policy', href: '/privacy-policy' },
  { title: 'Terms & Conditions', href: '/terms-and-conditions' },
];

function FooterLink({ href, external, children }) {
  if (external) {
    return (
      <a href={href} target='_blank' rel='noreferrer' className='text-neutral-400 hover:text-primary-500 transition-colors duration-200'>
        {children}
      </a>
    );
  }

  return (
    <Link href={href}>
      <a className='text-neutral-400 hover:text-primary-500 transition-colors duration-200'>{children}</a>
    </Link>
  );
}

function Footer() {
  return (
    <footer className='bg-gray-900 px-4 lg:px-8 py-12'>
      <div className='max-w-7xl mx-auto'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12'>
          <div>
            <h3 className='text-white font-bold text-lg mb-4'>Collections</h3>
            <ul className='space-y-2'>
              {COLLECTIONS_LINKS.map((link) => (
                <li key={link.href}>
                  <FooterLink href={link.href} external={link.external}>
                    {link.title}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className='text-white font-bold text-lg mb-4'>Utilities</h3>
            <ul className='space-y-2'>
              {UTILITIES_LINKS.map((link) => (
                <li key={link.href}>
                  <FooterLink href={link.href} external={link.external}>
                    {link.title}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className='text-white font-bold text-lg mb-4'>Community</h3>
            <ul className='space-y-2'>
              {COMMUNITY_LINKS.map((link) => (
                <li key={link.href}>
                  <FooterLink href={link.href} external={link.external}>
                    {link.title}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className='text-white font-bold text-lg mb-4'>Resources</h3>
            <ul className='space-y-2'>
              {RESOURCES_LINKS.map((link) => (
                <li key={link.href}>
                  <FooterLink href={link.href} external={link.external}>
                    {link.title}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className='border-t border-neutral-800 pt-8'>
          <div className='flex flex-col md:flex-row items-center justify-between gap-4'>
            <div className='flex items-center gap-6'>
              <a
                href={config.discord}
                target='_blank'
                rel='noreferrer'
                className='text-neutral-400 hover:text-white transition-colors duration-200'
                aria-label='Join Discord'
              >
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512' fill='currentColor' className='w-6 h-6'>
                  <path d='M524.531,69.836a1.5,1.5,0,0,0-.764-.7A485.065,485.065,0,0,0,404.081,32.03a1.816,1.816,0,0,0-1.923.91,337.461,337.461,0,0,0-14.9,30.6,447.848,447.848,0,0,0-134.426,0,309.541,309.541,0,0,0-15.135-30.6,1.89,1.89,0,0,0-1.924-.91A483.689,483.689,0,0,0,116.085,69.137a1.712,1.712,0,0,0-.788.676C39.068,183.651,18.186,294.69,28.43,404.354a2.016,2.016,0,0,0,.765,1.375A487.666,487.666,0,0,0,176.02,479.918a1.9,1.9,0,0,0,2.063-.676A348.2,348.2,0,0,0,208.12,430.4a1.86,1.86,0,0,0-1.019-2.588,321.173,321.173,0,0,1-45.868-21.853,1.885,1.885,0,0,1-.185-3.126c3.082-2.309,6.166-4.711,9.109-7.137a1.819,1.819,0,0,1,1.9-.256c96.229,43.917,200.41,43.917,295.5,0a1.812,1.812,0,0,1,1.924.233c2.944,2.426,6.027,4.851,9.132,7.16a1.884,1.884,0,0,1-.162,3.126,301.407,301.407,0,0,1-45.89,21.83,1.875,1.875,0,0,0-1,2.611,391.055,391.055,0,0,0,30.014,48.815,1.864,1.864,0,0,0,2.063.7A486.048,486.048,0,0,0,610.7,405.729a1.882,1.882,0,0,0,.765-1.352C623.729,277.594,590.933,167.465,524.531,69.836ZM222.491,337.58c-28.972,0-52.844-26.587-52.844-59.239S193.056,219.1,222.491,219.1c29.665,0,53.306,26.82,52.843,59.239C275.334,310.993,251.924,337.58,222.491,337.58Zm195.38,0c-28.971,0-52.843-26.587-52.843-59.239S388.437,219.1,417.871,219.1c29.667,0,53.307,26.82,52.844,59.239C470.715,310.993,447.538,337.58,417.871,337.58Z' />
                </svg>
              </a>
              <a
                href={config.twitter}
                target='_blank'
                rel='noreferrer'
                className='text-neutral-400 hover:text-white transition-colors duration-200'
                aria-label='Follow on 𝕏'
              >
                <svg viewBox='0 0 1200 1227' xmlns='http://www.w3.org/2000/svg' fill='currentColor' className='w-5 h-5'>
                  <path d='M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z' />
                </svg>
              </a>
              <a
                href='https://www.youtube.com/@Shitty_Kitties'
                target='_blank'
                rel='noreferrer'
                className='text-neutral-400 hover:text-white transition-colors duration-200'
                aria-label='Watch on YouTube'
              >
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512' fill='currentColor' className='w-6 h-6'>
                  <path d='M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z' />
                </svg>
              </a>
              <a
                href='https://www.instagram.com/shittykitties_'
                target='_blank'
                rel='noreferrer'
                className='text-neutral-400 hover:text-white transition-colors duration-200'
                aria-label='Follow on Instagram'
              >
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' fill='currentColor' className='w-6 h-6'>
                  <path d='M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z' />
                </svg>
              </a>
              <a
                href='https://www.tiktok.com/@shitty.kitties'
                target='_blank'
                rel='noreferrer'
                className='text-neutral-400 hover:text-white transition-colors duration-200'
                aria-label='Follow on TikTok'
              >
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' fill='currentColor' className='w-5 h-5'>
                  <path d='M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z' />
                </svg>
              </a>
            </div>

            <p className='text-neutral-400 text-sm text-center md:text-right'>
              {new Date().getFullYear()} {config.siteName} • An Algorand NFT Collection • Created by{' '}
              <a
                href='https://www.minnerlabs.com'
                target='_blank'
                rel='noreferrer'
                className='text-primary-500 hover:text-primary-400 transition-colors duration-200'
              >
                Minner Labs
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
